import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import type { RetakeReviewStatus } from "./hooks/useRetakesReviewWorkflow";

export type RetakeDraftItem = {
    id: string;
    startSec: number;
    endSec: number;
    confidence: number;
    removedText: string;
    keptText: string;
    reason?: string;
};

type RemoveRetakesSidebarProps = {
    onBack: () => void;
    onDetect?: () => void;
    onDone?: () => void;
    onAcceptAllRemaining?: () => void;
    onPreviewClick?: (id: string) => void;
    onAcceptOne?: (id: string) => void;
    onRejectOne?: (id: string) => void;
    isLoading?: boolean;
    isApplying?: boolean;
    hasRun?: boolean;
    error?: string | null;
    draft?: RetakeDraftItem[];
    statusById?: Record<string, RetakeReviewStatus>;
    busyById?: Record<string, boolean>;
    activeId?: string | null;
};

const formatMMSS = (seconds: number): string => {
    const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const truncate = (value: string, maxChars: number): string => {
    const cleaned = String(value ?? "").replace(/\s+/g, " ").trim();
    if (!cleaned) return "";
    if (cleaned.length <= maxChars) return cleaned;
    return `${cleaned.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;
};

const pct = (value: number): string => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0%";
    return `${Math.round(Math.max(0, Math.min(1, n)) * 100)}%`;
};

const RemoveRetakesSidebar: React.FC<RemoveRetakesSidebarProps> = ({
    onBack,
    onDetect,
    onDone,
    onAcceptAllRemaining,
    onPreviewClick,
    onAcceptOne,
    onRejectOne,
    isLoading,
    isApplying,
    hasRun,
    error,
    draft,
    statusById,
    busyById,
    activeId,
}) => {
    const hasDraft = Array.isArray(draft) && draft.length > 0;
    const detectButtonLabel = hasDraft ? "Redetect" : "Detect retakes";
    const isBusyGlobal = Boolean(isLoading || isApplying);

    const rows = useMemo(() => {
        if (!hasDraft) return [];
        return (draft ?? []).map((d, idx) => {
            const detectionId = String(d.id ?? "").trim() || `retake_${idx}`;
            return {
                key: `${idx}-${detectionId}`,
                id: detectionId,
                status: statusById?.[detectionId] ?? "pending",
                isBusy: Boolean(busyById?.[detectionId]),
                isActive: String(activeId ?? "") === detectionId,
                start: formatMMSS(Number(d.startSec ?? 0)),
                end: formatMMSS(Number(d.endSec ?? 0)),
                confidence: pct(Number(d.confidence ?? 0)),
                removedText: truncate(String(d.removedText ?? ""), 130),
                keptText: truncate(String(d.keptText ?? ""), 130),
                reason: truncate(String(d.reason ?? ""), 110),
            };
        });
    }, [activeId, busyById, draft, hasDraft, statusById]);

    const pendingCount = rows.filter((row) => row.status === "pending").length;
    const acceptedCount = rows.filter((row) => row.status === "accepted").length;
    const rejectedCount = rows.filter((row) => row.status === "rejected").length;

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                p: "3px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "8px" }}>
                <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                    <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--app-text-primary)" }}>
                    Remove retakes
                </Typography>
            </Box>

            <Box
                sx={{
                    backgroundColor: "var(--app-bg-surface)",
                    border: "1px solid var(--app-border-subtle)",
                    borderRadius: "12px",
                    padding: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                }}
            >
                <Typography sx={{ fontSize: 13, color: "var(--app-text-muted)" }}>
                    Detect obvious repeated takes conservatively, then accept or omit each suggestion.
                </Typography>

                <Button
                    fullWidth
                    onClick={onDetect}
                    disabled={isBusyGlobal || !onDetect}
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 500,
                        textTransform: "none",
                        color: "var(--app-bg-surface)",
                        backgroundColor: "var(--app-text-primary)",
                        borderRadius: "8px",
                        height: "40px",
                        "&:hover": {
                            backgroundColor: "#2B2B2B",
                        },
                        "&:disabled": {
                            backgroundColor: "var(--app-border-subtle)",
                            color: "var(--app-text-subtle)",
                        },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        {isBusyGlobal ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
                        <Box>{detectButtonLabel}</Box>
                    </Box>
                </Button>

                {!!error && (
                    <Box
                        sx={{
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            borderRadius: "10px",
                            px: "10px",
                            py: "8px",
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "#991B1B" }}>{error}</Typography>
                    </Box>
                )}

                {!error && !isBusyGlobal && hasRun && !hasDraft ? (
                    <Box
                        sx={{
                            backgroundColor: "var(--app-bg-subtle)",
                            border: "1px solid var(--app-border-subtle)",
                            borderRadius: "10px",
                            px: "10px",
                            py: "8px",
                        }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "var(--app-text-primary)" }}>
                            No obvious retakes detected. Detection stays conservative by default.
                        </Typography>
                    </Box>
                ) : null}
            </Box>

            {hasDraft ? (
                <Box
                    sx={{
                        backgroundColor: "var(--app-bg-surface)",
                        border: "1px solid var(--app-border-subtle)",
                        borderRadius: "12px",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--app-text-primary)" }}>Preview</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "var(--app-text-muted)" }}>
                            {rows.length} {rows.length === 1 ? "retake" : "retakes"} · {acceptedCount} accepted · {rejectedCount} omitted
                        </Typography>
                    </Box>
                    <Divider sx={{ borderColor: "var(--app-border-subtle)" }} />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", overflow: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
                        {rows.map((row) => (
                            <Box
                                key={row.key}
                                onClick={() => onPreviewClick?.(row.id)}
                                sx={{
                                    border:
                                        row.status === "accepted"
                                            ? "1px solid #86EFAC"
                                            : row.status === "rejected"
                                              ? "1px solid #FECACA"
                                              : row.isActive
                                                ? "1px solid #93C5FD"
                                                : "1px solid var(--app-border-subtle)",
                                    backgroundColor:
                                        row.status === "accepted"
                                            ? "#F0FDF4"
                                            : row.status === "rejected"
                                              ? "#FEF2F2"
                                              : row.isActive
                                                ? "#EFF6FF"
                                                : "var(--app-bg-surface)",
                                    borderRadius: "10px",
                                    px: "10px",
                                    py: "9px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                    cursor: "pointer",
                                    transition: "all 0.12s ease",
                                    "&:hover": {
                                        borderColor: row.status === "accepted" ? "#4ADE80" : row.status === "rejected" ? "#FCA5A5" : "#CBD5E1",
                                    },
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            color: "var(--app-text-muted)",
                                            flexShrink: 0,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {row.start} - {row.end}
                                    </Typography>
                                    <Box
                                        sx={{
                                            marginLeft: "auto",
                                            px: "7px",
                                            py: "1px",
                                            borderRadius: "999px",
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: "#92400E",
                                            backgroundColor: "#FEF3C7",
                                            border: "1px solid #FDE68A",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {row.confidence}
                                    </Box>
                                </Box>
                                <Typography sx={{ fontSize: 12.5, color: "#991B1B", lineHeight: 1.45 }}>
                                    Remove: {row.removedText || "(no text)"}
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: "#065F46", lineHeight: 1.45 }}>
                                    Keep: {row.keptText || "(no text)"}
                                </Typography>
                                {row.reason ? (
                                    <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)", lineHeight: 1.4 }}>
                                        {row.reason}
                                    </Typography>
                                ) : null}

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAcceptOne?.(row.id);
                                        }}
                                        disabled={isBusyGlobal || row.isBusy || !onAcceptOne}
                                        sx={{
                                            p: 0.45,
                                            border: "1px solid",
                                            borderColor: row.status === "accepted" ? "#16A34A" : "var(--app-border-muted)",
                                            color: row.status === "accepted" ? "#16A34A" : "var(--app-text-muted)",
                                            backgroundColor: row.status === "accepted" ? "#DCFCE7" : "var(--app-bg-surface)",
                                            borderRadius: "6px",
                                            "&:hover": { backgroundColor: row.status === "accepted" ? "#BBF7D0" : "var(--app-bg-subtle)" },
                                        }}
                                    >
                                        {row.isBusy ? <CircularProgress size={13} sx={{ color: "inherit" }} /> : <CheckRoundedIcon sx={{ fontSize: 14 }} />}
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRejectOne?.(row.id);
                                        }}
                                        disabled={isBusyGlobal || row.isBusy || !onRejectOne}
                                        sx={{
                                            p: 0.45,
                                            border: "1px solid",
                                            borderColor: row.status === "rejected" ? "#DC2626" : "var(--app-border-muted)",
                                            color: row.status === "rejected" ? "#DC2626" : "var(--app-text-muted)",
                                            backgroundColor: row.status === "rejected" ? "#FEE2E2" : "var(--app-bg-surface)",
                                            borderRadius: "6px",
                                            "&:hover": { backgroundColor: row.status === "rejected" ? "#FECACA" : "var(--app-bg-subtle)" },
                                        }}
                                    >
                                        <CloseRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ pt: "10px", mt: "2px", borderTop: "1px solid var(--app-border-subtle)", display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                            onClick={onDone}
                            disabled={isBusyGlobal || !onDone}
                            sx={{
                                flex: "0 0 auto",
                                fontFamily: "Inter",
                                fontSize: "13px",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "var(--app-text-primary)",
                                backgroundColor: "transparent",
                                border: "1px solid var(--app-border-subtle)",
                                borderRadius: "6px",
                                minHeight: "36px",
                                px: "12px",
                                "&:hover": { backgroundColor: "var(--app-bg-subtle)", borderColor: "var(--app-border-muted)" },
                                "&:disabled": { color: "var(--app-border-muted)", borderColor: "var(--app-bg-subtle)" },
                            }}
                        >
                            Done
                        </Button>
                        <Button
                            fullWidth
                            onClick={onAcceptAllRemaining}
                            disabled={isBusyGlobal || pendingCount <= 0 || !onAcceptAllRemaining}
                            sx={{
                                fontFamily: "Inter",
                                fontSize: "13px",
                                fontWeight: 500,
                                textTransform: "none",
                                color: "var(--app-bg-surface)",
                                backgroundColor: "var(--app-text-primary)",
                                borderRadius: "6px",
                                height: "36px",
                                "&:hover": {
                                    backgroundColor: "#1F2937",
                                },
                                "&:disabled": {
                                    backgroundColor: "var(--app-bg-subtle)",
                                    color: "var(--app-text-subtle)",
                                },
                            }}
                        >
                            Accept all above edits {pendingCount > 0 ? `(${pendingCount})` : ""}
                        </Button>
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
};

export default RemoveRetakesSidebar;
